/**
 * Générateur — Attendu 196 — O6 « Il intègre une variable dans un programme de
 * déplacement, de construction géométrique ou de calcul. »
 * Niveau : 4ème | Thème #12 Algorithmique (O6, Niveau 2) | Session A.
 * Source prof : O6 (Créé n°25 « la spirale à variable ») + CS1.Ex.5 « la variable
 * côté en tableur » (en écho, le tableur comme autre visage de la variable).
 *
 * ADAPTATIONS scoring : « donne les valeurs successives » → trous saisie (suite
 * arithmétique, valeurs distinctes). « quelle figure » → QCM dépendant de l'angle
 * (360/a côtés → carrée/triangulaire/hexagonale, calculable). « modifie une
 * valeur » → QCM (angle cible). Tableur → calcul du volume (formule montrée).
 *
 * 5 cartes :
 *  1. spirale_valeurs  — les valeurs successives de la variable (trous saisie).
 *  2. spirale_figure   — quelle figure trace la spirale ? (QCM).
 *  3. spirale_longueur — longueur totale parcourue (saisie).
 *  4. spirale_modif    — modifier une valeur pour changer de spirale (QCM).
 *  5. tableur          — la variable en tableur : volume d'une pyramide (saisie).
 */

import {
    randInt, randomChoice, makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { svgScript, imgElement } from './algo';

const ATTENDU_ID = 196;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_VALEURS = 'La spirale : les valeurs de la variable';
const L_FIGURE = 'La spirale : quelle figure ?';
const L_LONGUEUR = 'La spirale : la longueur totale';
const L_MODIF = 'La spirale : modifier pour changer de forme';
const L_TABLEUR = 'La variable en tableur : le volume';

// angle → nombre de côtés → nom de la spirale
const SPIRALES = { 120: 'triangulaire', 90: 'carrée', 60: 'hexagonale' };

const scriptSpirale = (s0, p, angle, n) => svgScript([
    { t: 'hat', txt: 'quand le drapeau est cliqué' },
    { t: 'variable', txt: `mettre côté à ${s0}` },
    { t: 'repeat', n, body: [
        { t: 'move', txt: 'avancer de côté pas' },
        { t: 'move', txt: `tourner ↻ de ${angle} degrés` },
        { t: 'variable', txt: `ajouter ${p} à côté` },
    ] },
]);

// ═════════ Carte 1 — les valeurs successives (trous saisie) ═════════

const genSpiraleValeurs = () => {
    const s0 = randInt(1, 5) * 10;
    const p = randInt(1, 4) * 10;
    const n = 8;
    const vals = Array.from({ length: n }, (_, i) => s0 + i * p);
    const phrase = 'Valeurs successives de « côté » : '
        + vals.map((v) => `{{${v}}}`).join(' ; ') + '.';
    const texte = '<p><b>Le lutin exécute ce script. Donne les valeurs successives prises par '
        + 'la variable « côté » pour avancer (dans l\'ordre).</b></p>';
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_VALEURS), mode: 'saisie', elements_additionnels: imgElement(scriptSpirale(s0, p, 90, n)) });
    payload._meta.params = { s0, p, n };
    return payload;
};

// ═════════ Carte 2 — quelle figure ? (QCM) ═════════

const genSpiraleFigure = () => {
    const angle = randomChoice([120, 90, 60]);
    const s0 = randInt(1, 5) * 10;
    const p = randInt(1, 4) * 10;
    const correct = `Une spirale ${SPIRALES[angle]}.`;
    const choix = [
        'Une spirale triangulaire.',
        'Une spirale carrée.',
        'Une spirale hexagonale.',
        'Un cercle parfait.',
    ];
    const texte = `<p><b>Le lutin exécute ce script (il tourne de ${angle}° à chaque étape). `
        + 'Quelle figure trace-t-il ?</b></p>';
    const payload = makeQcm(choix, correct, texte,
        { ...opts(L_FIGURE), elements_additionnels: imgElement(scriptSpirale(s0, p, angle, 8)) });
    payload._meta.params = { angle, s0, p };
    return payload;
};

// ═════════ Carte 3 — longueur totale (saisie) ═════════

const genSpiraleLongueur = () => {
    const s0 = randInt(1, 5) * 10;
    const p = randInt(1, 4) * 10;
    const n = randomChoice([6, 8, 10]);
    const total = n * s0 + p * (n * (n - 1)) / 2;   // suite arithmétique
    const texte = `<p><b>Le lutin exécute ce script. Quelle est la longueur totale parcourue `
        + '(la somme de toutes les longueurs « côté ») ?</b></p>';
    const payload = makeSaisie(String(total), texte,
        { ...opts(L_LONGUEUR), elements_additionnels: imgElement(scriptSpirale(s0, p, 90, n)) });
    payload._meta.params = { s0, p, n, total };
    return payload;
};

// ═════════ Carte 4 — modifier pour changer de forme (QCM) ═════════

const genSpiraleModif = () => {
    // spirale actuelle carrée (90) → cible triangulaire ou hexagonale
    const cible = randomChoice([120, 60]);
    const s0 = randInt(1, 5) * 10;
    const p = randInt(1, 4) * 10;
    // Flèche ↻ dans TOUTES les citations de blocs (2026-07-28) : l'image du
    // script montre « tourner ↻ … » — les choix qui le citent doivent être
    // identiques, et la flèche est posée sur tous les choix pour ne pas
    // désigner la bonne réponse.
    const correct = `Remplacer « tourner ↻ de 90 degrés » par « tourner ↻ de ${cible} degrés ».`;
    const choix = [
        'Remplacer « tourner ↻ de 90 degrés » par « tourner ↻ de 120 degrés ».',
        'Remplacer « tourner ↻ de 90 degrés » par « tourner ↻ de 60 degrés ».',
        `Remplacer « ajouter ${p} à côté » par « ajouter ${p + 10} à côté ».`,
        'Remplacer « répéter 8 fois » par « répéter 6 fois ».',
    ];
    const texte = `<p><b>Ce script trace une spirale carrée. Quelle SEULE valeur modifier pour `
        + `obtenir une spirale ${SPIRALES[cible]} ?</b></p>`;
    const payload = makeQcm(choix, correct, texte,
        { ...opts(L_MODIF), elements_additionnels: imgElement(scriptSpirale(s0, p, 90, 8)) });
    payload._meta.params = { cible, s0, p };
    return payload;
};

// ═════════ Carte 5 — la variable en tableur (saisie) ═════════

const genTableur = () => {
    const cote = randInt(2, 9);
    const hauteur = randInt(1, 6) * 3;   // multiple de 3 → volume entier
    const volume = (cote * cote * hauteur) / 3;
    const texte = '<p><b>Dans un tableur, une pyramide à base carrée a son côté en A2 et sa '
        + `hauteur en B2. On saisit en A2 la valeur ${cote}, en B2 la valeur ${hauteur}.</b></p>`
        + '<p>La cellule C2 contient la formule <b>= A2 × A2 × B2 ÷ 3</b> (le volume). '
        + 'Quelle valeur affiche C2 ?</p>';
    const payload = makeSaisie(String(volume), texte, opts(L_TABLEUR));
    payload._meta.params = { cote, hauteur, volume };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    spirale_valeurs_texte_trou_saisie: genSpiraleValeurs,
    spirale_figure_qcm_unique: genSpiraleFigure,
    spirale_longueur_texte_saisie: genSpiraleLongueur,
    spirale_modif_qcm_unique: genSpiraleModif,
    tableur_texte_saisie: genTableur,
};

const CARTES = [
    { id: 'spirale_valeurs', label: L_VALEURS },
    { id: 'spirale_figure', label: L_FIGURE },
    { id: 'spirale_longueur', label: L_LONGUEUR },
    { id: 'spirale_modif', label: L_MODIF },
    { id: 'tableur', label: L_TABLEUR },
];

export const CATEGORIES = CARTES.map((c) => ({
    id: c.id,
    label: c.label,
    formatsList: Object.keys(GENERATORS).filter((k) => k.startsWith(`${c.id}_`))
        .map((k) => ({ key: k, label: c.label })),
}));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
